import { Callout } from "@/components/Callout/Callout";
import { useToasts } from "@/context/toasts";
import { AnimatePresence, motion } from "motion/react";
import { Toast as RadixToast } from "radix-ui";
import React from "react";
import css from "./Toasts.module.scss";

type Props = {
  id: string;
  type: "info" | "success" | "error";
  message: string;
  /**
   * How long to show the toast in milliseconds
   */
  duration?: number;
  onOpenChange?: (open: boolean) => void;
};

export function ToastList() {
  const { toasts } = useToasts();

  return (
    <>
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
      </AnimatePresence>

      <div className={css.viewportContainer}>
        <div className="l-wrapper">
          <RadixToast.Viewport className={css.list} data-testid="toasts" />
        </div>
      </div>
    </>
  );
}

export function Toast({ id, type, message, duration, onOpenChange }: Props) {
  const { removeToast } = useToasts();

  return (
    <RadixToast.Root
      duration={duration}
      asChild
      forceMount
      open
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }

        removeToast(id);
      }}
    >
      <motion.li
        className={css.toast}
        layout
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50, transition: { duration: 0.2 } }}
      >
        <Callout
          variant={callouts[type].variant}
          icon={callouts[type].icon}
          onClose={() => {
            removeToast(id);
          }}
        >
          <p>{message}</p>
        </Callout>
      </motion.li>
    </RadixToast.Root>
  );
}

// Map of toast types to callout styles
const callouts = {
  info: {
    variant: "yellow",
    icon: "key",
  },
  success: {
    variant: "green",
    icon: "key",
  },
  error: {
    variant: "red",
    icon: "key",
  },
} as const;
